import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Breadcrumbs,
  Link,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Sheet,
  Table,
  LinearProgress,
} from '@mui/joy';
import { FeatureGate } from '../../components/common/FeatureGate';
import {
  ArrowLeft,
  FileText,
  MapPin,
  Calendar,
  User,
  PencilSimple as Edit,
  Trash as Trash2,
  DownloadSimple as Download,
  Scan,
} from '@phosphor-icons/react';
import {
  DrawableGoogleMap,
  EagleViewPanel,
  EagleViewOrderModal,
  FacetVisualization,
  EagleViewStatusChip,
  LinearTotalsPanel,
} from '../../components/measurements';
import { PITCH_MULTIPLIERS } from '../../components/measurements/lineDrawingConstants';
import { VentilationPlanPanel } from '../../components/measurements/ventilation/VentilationPlanPanel';
import { MaterialTakeoffPanel } from '../../components/measurements/MaterialTakeoffPanel';
import {
  useMeasurement,
  useRoofSections,
  useAnalyzeGeometry,
  useGenerateReport,
  useDeleteMeasurement,
  useRoofMeasurementCoverage,
  useRoofMeasurementFullMeasurement,
  useRoofMeasurementGenerateReport,
  useRoofMeasurementReportStatus,
  useRoofMeasurementReportDownloadUrl,
} from '../../hooks/measurements';
import { useRoofFeatures, useGenerateReportFromMeasurement, useSyncLinearTotals } from '../../hooks/measurements/useRoofFeatures';
import {
  useAutoDrawStatus,
  useAcceptAutoDraw,
  useRejectAutoDraw,
} from '../../hooks/measurements/useAutoDraw';
import { AutoDrawReviewBanner } from '../../components/measurements/AutoDrawReviewBanner';
import { useDetailedMeasurements } from '../../hooks/measurements/useDetailedMeasurements';
import type { RoofSection, RoofMeasurementMeasurementResponse } from '../../types';

const MeasurementDetailPageContent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const measurementId = parseInt(id || '0');
  
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRoofSection, setSelectedRoofSection] = useState<RoofSection | null>(null);
  const [showEagleViewOrderModal, setShowEagleViewOrderModal] = useState(false);
  const [eagleViewData, setEagleViewData] = useState<any>(null);
  const [roofMeasurementResult, setRoofMeasurementResult] = useState<RoofMeasurementMeasurementResponse | null>(null);
  const [roofMeasurementReportId, setRoofMeasurementReportId] = useState<string | undefined>(undefined);

  const { data: measurement, isLoading } = useMeasurement(measurementId);
  const { data: roofSections = [] } = useRoofSections(measurementId);
  const { data: roofFeatures = [] } = useRoofFeatures(measurementId);
  const { data: detailedMeasurements = [] } = useDetailedMeasurements(measurementId);
  const analyzeGeometryMutation = useAnalyzeGeometry();
  const generateReportMutation = useGenerateReport();
  const generateReportFromMeasurementMutation = useGenerateReportFromMeasurement();
  const deleteMutation = useDeleteMeasurement();
  const syncLinearTotalsMutation = useSyncLinearTotals();
  const [generatingReport, setGeneratingReport] = useState(false);

  // Auto-draw review state (detected geometry awaiting accept/discard)
  const { data: autoDrawRun } = useAutoDrawStatus(measurementId);
  const acceptAutoDrawMutation = useAcceptAutoDraw();
  const rejectAutoDrawMutation = useRejectAutoDraw();
  const autoDrawPendingReview = autoDrawRun?.status === 'completed';

  // Roof measurement hooks (aerial imagery)
  const { data: roofMeasurementCoverage } = useRoofMeasurementCoverage(
    measurement ? Number(measurement.latitude) : undefined,
    measurement ? Number(measurement.longitude) : undefined
  );
  const roofMeasurementFullMutation = useRoofMeasurementFullMeasurement();
  const roofMeasurementReportMutation = useRoofMeasurementGenerateReport();
  const { data: roofMeasurementReportStatus } = useRoofMeasurementReportStatus(roofMeasurementReportId);
  const roofMeasurementDownloadUrl = useRoofMeasurementReportDownloadUrl(roofMeasurementReportId);

  const handleRoofMeasurementAutoDetect = async () => {
    if (!measurement) return;
    try {
      const result = await roofMeasurementFullMutation.mutateAsync({
        lat: Number(measurement.latitude),
        lon: Number(measurement.longitude),
      });
      setRoofMeasurementResult(result);
    } catch (error) {
      console.error('Roof measurement auto-detect failed:', error);
    }
  };

  const handleRoofMeasurementReport = async () => {
    if (!measurement) return;
    try {
      const result = await roofMeasurementReportMutation.mutateAsync({
        address: measurement.address,
        lat: Number(measurement.latitude),
        lon: Number(measurement.longitude),
        polygon: roofMeasurementResult?.building_polygon || undefined,
        survey_id: roofMeasurementResult?.survey_id || undefined,
      });
      setRoofMeasurementReportId(result.report_id);
    } catch (error) {
      console.error('Roof measurement report generation failed:', error);
    }
  };

  const handleAnalyzeGeometry = async () => {
    try {
      await analyzeGeometryMutation.mutateAsync(measurementId);
      alert('Roof geometry analysis completed successfully!');
    } catch (error) {
      console.error('Failed to analyze geometry:', error);
      alert('Failed to analyze roof geometry. Please try again.');
    }
  };

  const handleGenerateReport = async () => {
    try {
      const result = await generateReportMutation.mutateAsync({
        measurementId,
        options: {
          report_type: 'detailed',
          title: `Measurement Report - ${measurement?.address}`,
          include_materials: true,
          custom_options: {
            include_photos: true,
            include_detailed_calculations: true,
          },
        },
      });
      
      if (result && typeof result === 'object' && 'download_urls' in result) {
        const downloadUrls = (result as any).download_urls;
        if (downloadUrls?.pdf) {
          window.open(downloadUrls.pdf, '_blank');
        }
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
      alert('Failed to generate report. Please try again.');
    }
  };

  const handleEdit = () => {
    // Navigate to measurements page in edit mode
    navigate('/operations/measurements', { state: { editMeasurementId: measurementId } });
  };

  const handleDelete = async () => {
    if (!measurement) return;
    
    if (window.confirm(`Are you sure you want to delete the measurement for ${measurement.address}?`)) {
      try {
        await deleteMutation.mutateAsync(measurementId);
        // Navigate back to measurements list after successful deletion
        navigate('/');
      } catch (error) {
        console.error('Failed to delete measurement:', error);
        alert('Failed to delete measurement. Please try again.');
      }
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!measurement) {
    return (
      <Box sx={{ width: '100%' }}>
        <Alert color="danger">
          <Typography>Measurement not found</Typography>
        </Alert>
        <Button
          variant="soft"
          color="neutral"
          startDecorator={<ArrowLeft size={18} />}
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Back to Measurements
        </Button>
      </Box>
    );
  }

  const enhancedRoofSections = (roofSections || []).map(section => ({
    ...section,
    faces: [],
    features: [],
  }));

  return (
    <Box sx={{ width: '100%', p: { xs: 1.5, md: 0 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            onClick={() => navigate('/')}
            sx={{ color: 'text.primary' }}
          >
            Measurements
          </Link>
          <Typography>Details</Typography>
        </Breadcrumbs>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'flex-start' } }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography level="h2" sx={{ mb: 1, fontSize: { xs: '1.5rem', md: undefined }, wordBreak: 'break-word' }}>
              {measurement.address}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip
                variant="soft"
                color={
                  measurement.status === 'completed' ? 'success' :
                  measurement.status === 'in_progress' ? 'primary' : 'neutral'
                }
              >
                {measurement.status.replace('_', ' ').toUpperCase()}
              </Chip>
              <EagleViewStatusChip status={measurement.eagleview_status} />
              <Typography level="body-sm" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <MapPin size={16} />
                {Number(measurement.latitude).toFixed(6)}, {Number(measurement.longitude).toFixed(6)}
              </Typography>
              <Typography level="body-sm" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Calendar size={16} />
                {new Date(measurement.created_at).toLocaleDateString()}
              </Typography>
              <Typography level="body-sm" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <User size={16} />
                {measurement.created_by_name}
              </Typography>
            </Box>
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', '& > button': { flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' } } }}>
            <Button
              variant="solid"
              color="success"
              startDecorator={<FileText size={18} />}
              onClick={() => setActiveTab(3)}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Generate&nbsp;</Box>Takeoff
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<Edit size={18} />}
              onClick={handleEdit}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="danger"
              startDecorator={<Trash2 size={18} />}
              onClick={handleDelete}
              loading={deleteMutation.isPending}
            >
              Delete
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* Tabbed Content */}
      <Card variant="outlined">
        <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value as number)}>
          <TabList sx={{ flexWrap: 'wrap', '& > *': { flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' } } }}>
            <Tab><Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Roof&nbsp;</Box>Map</Tab>
            <Tab><Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Measurement&nbsp;</Box>Report</Tab>
            <Tab value={3}><Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Material&nbsp;</Box>Takeoff</Tab>
            <Tab value={6}>Ventilation</Tab>
            {/* <Tab>Aerial AI</Tab> */}
            {/* <Tab>EagleView</Tab> */}
          </TabList>

          {/* Temporarily hidden Overview panel */}
          {/* <TabPanel value={0} sx={{ p: { xs: 1.5, md: 3 } }}>
            <AnalysisResultsDashboard
              measurementId={measurementId}
              onViewDetails={(section) => {
                switch (section) {
                  case 'faces':
                    setActiveTab(3);
                    break;
                  case 'features':
                    setActiveTab(3);
                    break;
                  case 'reports':
                    setActiveTab(4);
                    break;
                  default:
                    break;
                }
              }}
            />
          </TabPanel> */}

          <TabPanel value={0} sx={{ p: { xs: 1.5, md: 3 } }}>
            <Typography level="h4" sx={{ mb: 2 }}>
              Roof Sections Map
            </Typography>

            {autoDrawRun && ['completed', 'failed'].includes(autoDrawRun.status) && (
              <AutoDrawReviewBanner
                run={autoDrawRun}
                onAccept={() =>
                  acceptAutoDrawMutation.mutate({ measurementId, runId: autoDrawRun.id })
                }
                onReject={() =>
                  rejectAutoDrawMutation.mutate({ measurementId, runId: autoDrawRun.id })
                }
                accepting={acceptAutoDrawMutation.isPending}
                rejecting={rejectAutoDrawMutation.isPending}
              />
            )}

            {roofSections.length > 0 || roofFeatures.length > 0 ? (
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Typography level="body-sm">
                    Total Sections: {roofSections.length} |
                    Total Area: {roofSections.reduce((sum, s) => sum + Number(s.area_sqft || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} sq ft
                    {roofFeatures.length > 0 && ` | Lines: ${roofFeatures.length}`}
                  </Typography>
                </Box>

                <DrawableGoogleMap
                  selectedLocation={{ lat: Number(measurement.latitude), lng: Number(measurement.longitude) }}
                  height={typeof window !== 'undefined' && window.matchMedia('(max-width: 959.95px)').matches ? 400 : 600}
                  roofSections={roofSections}
                  roofFeatures={roofFeatures}
                  measurementId={measurementId}
                  readOnly={true}
                  reviewPending={autoDrawPendingReview}
                />

                {roofFeatures.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <LinearTotalsPanel features={roofFeatures} />
                  </Box>
                )}
              </Box>
            ) : (
              <Alert color="neutral">
                <Typography>No roof sections drawn yet. Go back to create measurement to draw roof sections.</Typography>
              </Alert>
            )}
          </TabPanel>

          {/* Measurement Report Tab */}
          <TabPanel value={1} sx={{ p: { xs: 1.5, md: 3 } }}>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography level="h4">Measurement Report</Typography>
                {detailedMeasurements.some(dm => dm.parsing_status === 'completed') && (
                  <Button
                    variant="solid"
                    color="primary"
                    startDecorator={<FileText size={18} />}
                    loading={generatingReport}
                    disabled={autoDrawPendingReview}
                    title={autoDrawPendingReview ? 'Review auto-drawn measurements first' : undefined}
                    onClick={async () => {
                      const dm = detailedMeasurements.find(d => d.parsing_status === 'completed');
                      if (!dm) return;
                      setGeneratingReport(true);
                      try {
                        const blob = await generateReportFromMeasurementMutation.mutateAsync(dm.id);
                        const url = URL.createObjectURL(blob);
                        window.open(url, '_blank');
                      } catch (err) {
                        console.error('Report generation failed:', err);
                        alert(`Failed to generate report: ${err instanceof Error ? err.message : 'Please try again.'}`);
                      } finally {
                        setGeneratingReport(false);
                      }
                    }}
                  >
                    Generate PDF Report
                  </Button>
                )}
              </Box>

              {detailedMeasurements.some(dm => dm.parsing_status === 'completed') ? (
                (() => {
                  const dm = detailedMeasurements.find(d => d.parsing_status === 'completed')!;
                  const flatArea = Number(dm.total_area_sqft || 0);
                  const pitchMultiplier = dm.predominant_pitch ? (PITCH_MULTIPLIERS[dm.predominant_pitch] || 1) : 1;
                  const trueArea = flatArea * pitchMultiplier;
                  const waste = Number(dm.waste_factor || 0);
                  const withWaste = trueArea * (1 + waste / 100);
                  // Apply pitch coefficient to sloped linear measurements (hip, valley, rake)
                  const adjustedHipFt = Number(dm.total_hips_ft || 0) * pitchMultiplier;
                  const adjustedValleyFt = Number(dm.total_valleys_ft || 0) * pitchMultiplier;
                  const adjustedRakeFt = Number(dm.total_rakes_ft || 0) * pitchMultiplier;
                  // Use polygon perimeter from roof sections if available, otherwise fall back to drawn lines
                  const polygonPerimeter = roofSections.reduce((sum, s) => sum + Number(s.perimeter_ft || 0), 0);
                  const eavePerimeter = polygonPerimeter || (Number(dm.total_eaves_ft || 0) + adjustedRakeFt);

                  return (
                    <>
                      {/* Area Summary */}
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', flex: '1 1 120px', textAlign: 'center' }}>
                          <Typography level="body-xs" textColor="text.tertiary">Flat Area</Typography>
                          <Typography level="h3">{flatArea.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                          <Typography level="body-xs">sq ft</Typography>
                        </Sheet>
                        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', flex: '1 1 120px', textAlign: 'center' }}>
                          <Typography level="body-xs" textColor="text.tertiary">True Area</Typography>
                          <Typography level="h3">{trueArea.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                          <Typography level="body-xs">sq ft (×{pitchMultiplier})</Typography>
                        </Sheet>
                        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', flex: '1 1 120px', textAlign: 'center' }}>
                          <Typography level="body-xs" textColor="text.tertiary">Squares</Typography>
                          <Typography level="h3">{(trueArea / 100).toFixed(1)}</Typography>
                        </Sheet>
                        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', flex: '1 1 120px', textAlign: 'center' }}>
                          <Typography level="body-xs" textColor="text.tertiary">Pitch</Typography>
                          <Typography level="h3">{dm.predominant_pitch || 'N/A'}</Typography>
                        </Sheet>
                        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', flex: '1 1 120px', textAlign: 'center' }}>
                          <Typography level="body-xs" textColor="text.tertiary">With Waste</Typography>
                          <Typography level="h3">{withWaste.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                          <Typography level="body-xs">sq ft (+{waste}%)</Typography>
                        </Sheet>
                      </Box>

                      {/* Linear Measurements */}
                      <Card variant="outlined">
                        <Typography level="title-md" sx={{ p: 2, pb: 0 }}>Linear Measurements</Typography>
                        <Table stripe="even" sx={{ '& th': { textTransform: 'uppercase', fontSize: 'xs' }, '& td:last-child, & th:last-child': { textAlign: 'right' } }}>
                          <thead>
                            <tr>
                              <th>Type</th>
                              <th>Total (ft)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Number(dm.total_ridges_ft || 0) > 0 && (
                              <tr><td>Ridge</td><td>{Number(dm.total_ridges_ft).toFixed(1)}</td></tr>
                            )}
                            {adjustedHipFt > 0 && (
                              <tr><td>Hip</td><td>{adjustedHipFt.toFixed(1)}</td></tr>
                            )}
                            {adjustedValleyFt > 0 && (
                              <tr><td>Valley</td><td>{adjustedValleyFt.toFixed(1)}</td></tr>
                            )}
                            {Number(dm.total_eaves_ft || 0) > 0 && (
                              <tr><td>Eave</td><td>{Number(dm.total_eaves_ft).toFixed(1)}</td></tr>
                            )}
                            {adjustedRakeFt > 0 && (
                              <tr><td>Rake</td><td>{adjustedRakeFt.toFixed(1)}</td></tr>
                            )}
                            <tr>
                              <td><strong>Eave/Perimeter</strong></td>
                              <td><strong>{eavePerimeter.toFixed(1)}</strong></td>
                            </tr>
                          </tbody>
                        </Table>
                      </Card>

                      {/* Source info */}
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        Source: {dm.source_type_display} | Updated: {new Date(dm.updated_at).toLocaleString()}
                      </Typography>
                    </>
                  );
                })()
              ) : (
                <Stack spacing={2}>
                  <Alert color="neutral">
                    <Typography level="body-sm">
                      No measurement data available yet.
                      {roofFeatures.length > 0
                        ? ' Click "Sync Measurement Data" below to generate it from your drawn lines.'
                        : ' Draw roof sections and lines, then click "Save & Continue" to generate measurement data.'}
                    </Typography>
                  </Alert>
                  {roofFeatures.length > 0 && (
                    <Button
                      variant="solid"
                      color="primary"
                      loading={syncLinearTotalsMutation.isPending}
                      onClick={async () => {
                        try {
                          await syncLinearTotalsMutation.mutateAsync({ measurementId });
                        } catch (error) {
                          console.error('Failed to sync:', error);
                          alert('Failed to sync measurement data.');
                        }
                      }}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Sync Measurement Data
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          </TabPanel>

          {/* Temporarily hidden Pitch Analysis panel */}
          {/* <TabPanel value={2} sx={{ p: { xs: 1.5, md: 3 } }}>
            <PitchAnalysisPanel
              measurementId={measurementId}
              onAnalysisComplete={() => {
                // Refresh data if needed
              }}
            />
          </TabPanel> */}

          {/* Temporarily hidden Faces & Features panel */}

          {/* Temporarily hidden Reports panel */}
          {/* <TabPanel value={4} sx={{ p: { xs: 1.5, md: 3 } }}>
            <Typography level="h4" sx={{ mb: 3 }}>
              Reports & Documents
            </Typography>
            
            <Grid container spacing={3}>
              <Grid xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography level="title-lg" sx={{ mb: 2 }}>
                      Quick Report Generation
                    </Typography>
                    
                    <Stack spacing={2}>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        onClick={() => generateReportMutation.mutate({
                          measurementId,
                          options: { report_type: 'standard', title: 'Standard Measurement Report' }
                        })}
                      >
                        Standard Report
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        onClick={() => generateReportMutation.mutate({
                          measurementId,
                          options: { report_type: 'material_list', title: 'Material List Report' }
                        })}
                      >
                        Material List Only
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        onClick={() => generateReportMutation.mutate({
                          measurementId,
                          options: { report_type: 'executive', title: 'Executive Summary' }
                        })}
                      >
                        Executive Summary
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography level="title-lg" sx={{ mb: 2 }}>
                      Export Options
                    </Typography>
                    
                    <Alert color="primary" sx={{ mb: 2 }}>
                      Reports include all measurement data, roof analysis, material calculations, and cost estimates.
                    </Alert>
                    
                    <Typography level="body-sm" sx={{ mb: 1 }}>
                      Available formats:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip variant="soft">PDF</Chip>
                      <Chip variant="soft">Excel</Chip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel> */}

          {/* Material Takeoff Panel */}
          <TabPanel value={3} sx={{ p: { xs: 1.5, md: 3 } }}>
            <MaterialTakeoffPanel
              measurement={measurement}
              detailedMeasurement={detailedMeasurements?.[0] || null}
            />
          </TabPanel>

          <TabPanel value={6} sx={{ p: { xs: 1.5, md: 3 } }}>
            <VentilationPlanPanel measurement={measurement} />
          </TabPanel>

          {/* Aerial AI Panel */}
          <TabPanel value={4} sx={{ p: { xs: 1.5, md: 3 } }}>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography level="h4">Aerial AI Roof Detection</Typography>
                  <Typography level="body-sm" sx={{ mt: 0.5 }}>
                    Auto-detect building footprint and measure roof geometry using HD aerial imagery.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="solid"
                    color="primary"
                    startDecorator={<Scan size={18} />}
                    onClick={handleRoofMeasurementAutoDetect}
                    loading={roofMeasurementFullMutation.isPending}
                    disabled={!roofMeasurementCoverage?.has_coverage}
                  >
                    Auto-Detect Roof
                  </Button>
                  {roofMeasurementResult && (
                    <Button
                      variant="outlined"
                      color="primary"
                      startDecorator={<FileText size={18} />}
                      onClick={handleRoofMeasurementReport}
                      loading={roofMeasurementReportMutation.isPending}
                    >
                      Generate PDF Report
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    color="primary"
                    startDecorator={<Edit size={18} />}
                    onClick={() => navigate(`/measurements/${measurementId}/roof-measurement-editor`)}
                    disabled={!roofMeasurementCoverage?.has_coverage}
                  >
                    Open in Drawing Editor
                  </Button>
                </Stack>
              </Box>

              {/* Coverage status */}
              {roofMeasurementCoverage && (
                <Alert color={roofMeasurementCoverage.has_coverage ? 'success' : 'warning'}>
                  {roofMeasurementCoverage.has_coverage
                    ? `Aerial coverage available — ${roofMeasurementCoverage.surveys.length} survey(s), latest: ${roofMeasurementCoverage.surveys[0]?.capture_date || 'Unknown'}`
                    : 'No aerial coverage for this location.'}
                </Alert>
              )}

              {/* Error states */}
              {roofMeasurementFullMutation.isError && (
                <Alert color="danger">
                  Auto-detection failed. {(roofMeasurementFullMutation.error as Error)?.message || 'Please try again.'}
                </Alert>
              )}

              {/* Results */}
              {roofMeasurementResult && (
                <>
                  {/* Summary cards */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', flex: '1 1 140px', textAlign: 'center' }}>
                      <Typography level="body-xs" textColor="text.tertiary">Total Area</Typography>
                      <Typography level="h3">{roofMeasurementResult.total_area_sqft.toLocaleString()}</Typography>
                      <Typography level="body-xs">sq ft</Typography>
                    </Sheet>
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', flex: '1 1 140px', textAlign: 'center' }}>
                      <Typography level="body-xs" textColor="text.tertiary">Pitch</Typography>
                      <Typography level="h3">{roofMeasurementResult.predominant_pitch || 'N/A'}</Typography>
                    </Sheet>
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', flex: '1 1 140px', textAlign: 'center' }}>
                      <Typography level="body-xs" textColor="text.tertiary">Facets</Typography>
                      <Typography level="h3">{roofMeasurementResult.facets.length || 'Not measured'}</Typography>
                    </Sheet>
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', flex: '1 1 140px', textAlign: 'center' }}>
                      <Typography level="body-xs" textColor="text.tertiary">Lines</Typography>
                      <Typography level="h3">{roofMeasurementResult.lines.length}</Typography>
                    </Sheet>
                  </Box>

                  {/* Line measurements table */}
                  <Card variant="outlined">
                    <Typography level="title-md" sx={{ p: 2, pb: 0 }}>Line Measurements</Typography>
                    <Table stripe="even" sx={{ '& th': { textTransform: 'uppercase', fontSize: 'xs' }, '& td:last-child, & th:last-child': { textAlign: 'right' } }}>
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Total (ft)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td>Ridge</td><td>{roofMeasurementResult.total_ridges_ft.toFixed(1)}</td></tr>
                        <tr><td>Hip</td><td>{roofMeasurementResult.total_hips_ft.toFixed(1)}</td></tr>
                        <tr><td>Valley</td><td>{roofMeasurementResult.total_valleys_ft.toFixed(1)}</td></tr>
                        <tr><td>Eave</td><td>{roofMeasurementResult.total_eaves_ft.toFixed(1)}</td></tr>
                        <tr><td>Rake</td><td>{roofMeasurementResult.total_rakes_ft.toFixed(1)}</td></tr>
                      </tbody>
                    </Table>
                  </Card>

                  {/* Warnings */}
                  {roofMeasurementResult.accuracy_warnings.length > 0 && (
                    <Alert color="warning">
                      <Typography level="title-sm">Accuracy Warnings</Typography>
                      {roofMeasurementResult.accuracy_warnings.map((w, i) => (
                        <Typography key={i} level="body-sm">{w}</Typography>
                      ))}
                    </Alert>
                  )}
                </>
              )}

              {/* Report download */}
              {roofMeasurementReportId && roofMeasurementReportStatus && (
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography level="title-md" sx={{ mb: 1 }}>PDF Report</Typography>
                  {roofMeasurementReportStatus.status === 'completed' ? (
                    <Button
                      component="a"
                      href={roofMeasurementDownloadUrl || '#'}
                      variant="solid"
                      color="success"
                      startDecorator={<Download size={18} />}
                    >
                      Download Report ({((roofMeasurementReportStatus.file_size_bytes || 0) / 1024).toFixed(0)} KB)
                    </Button>
                  ) : roofMeasurementReportStatus.status === 'failed' ? (
                    <Alert color="danger">
                      Report generation failed: {roofMeasurementReportStatus.error_message}
                    </Alert>
                  ) : (
                    <Box>
                      <Typography level="body-sm" sx={{ mb: 1 }}>
                        {roofMeasurementReportStatus.status === 'processing' ? 'Generating report...' : 'Queued...'}
                      </Typography>
                      <LinearProgress />
                    </Box>
                  )}
                </Card>
              )}
            </Stack>
          </TabPanel>

          {/* EagleView Panel */}
          <TabPanel value={5} sx={{ p: { xs: 1.5, md: 3 } }}>
            <Stack spacing={3}>
              {/* Order Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="solid"
                  color="primary"
                  startDecorator={<FileText size={18} />}
                  onClick={() => setShowEagleViewOrderModal(true)}
                >
                  Order Professional Report
                </Button>
              </Box>

              {/* EagleView Data Panel */}
              <EagleViewPanel
                measurementId={measurementId}
                onDataUpdated={() => setEagleViewData(Date.now())}
                showOrderButton={false}
              />

              {/* Facet Visualization (if data available) */}
              {eagleViewData && (
                <FacetVisualization
                  facets={eagleViewData.facets || []}
                  center={{
                    lat: Number(measurement.latitude),
                    lng: Number(measurement.longitude)
                  }}
                  visible={true}
                />
              )}
            </Stack>
          </TabPanel>
        </Tabs>
      </Card>

      {/* EagleView Order Modal */}
      {showEagleViewOrderModal && (
        <EagleViewOrderModal
          open={showEagleViewOrderModal}
          onClose={() => setShowEagleViewOrderModal(false)}
          measurementId={measurementId}
          address={measurement.address}
          onOrderPlaced={(orderData) => {
            setShowEagleViewOrderModal(false);
            setEagleViewData(Date.now()); // Trigger refresh
            alert('EagleView order placed successfully!');
          }}
        />
      )}
    </Box>
  );
};

export const MeasurementDetailPage: React.FC = () => {
  return (
    <FeatureGate
      requiredTiers={['premium', 'enterprise']}
      featureName="Advanced Measurements"
      featureDescription="Professional roof measurement tools with satellite imagery, precise calculations, and integration with EagleView for accurate estimates."
      benefits={[
        'Satellite imagery and aerial measurements',
        'Interactive drawing and editing tools',
        'Precise area and perimeter calculations',
        'Multiple roof section management',
        'EagleView integration for professional reports',
        'Automatic material calculations',
        'Export to estimates and invoices',
        'Historical measurement tracking'
      ]}
    >
      <MeasurementDetailPageContent />
    </FeatureGate>
  );
};